import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [tokenStatus, setTokenStatus] = useState('checking'); // checking | valid | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid');
      return;
    }
    axios.get(`${API}/auth/reset-password/verify/${token}`)
      .then(res => {
        setTokenStatus(res.data.valid ? 'valid' : 'invalid');
      })
      .catch(() => setTokenStatus('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #FAFAFA 0%, #FFF7ED 100%)'}}>
      <Card className="w-full max-w-md bg-white border-[#E5E5E5] shadow-2xl" data-testid="reset-password-card">
        <CardHeader className="space-y-2">
          <CardTitle className="text-3xl font-heading font-bold text-[#0A0A0A]">Nueva contraseña</CardTitle>
          <CardDescription className="text-[#737373]">
            Crea una nueva contraseña segura
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {tokenStatus === 'checking' && (
            <div className="text-center py-8 text-[#737373]" data-testid="reset-checking">
              Verificando token...
            </div>
          )}

          {tokenStatus === 'invalid' && (
            <div className="space-y-4 text-center py-4" data-testid="reset-invalid">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h3 className="text-lg font-semibold text-[#0A0A0A]">Enlace inválido o expirado</h3>
              <p className="text-sm text-[#737373]">
                El enlace de recuperación no es válido o ha expirado. Solicita uno nuevo.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="request-new-link-button">
                  Solicitar nuevo enlace
                </Button>
              </Link>
            </div>
          )}

          {tokenStatus === 'valid' && !success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#0A0A0A]">Nueva contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary"
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-[#0A0A0A]">Confirmar contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="confirm"
                    type="password"
                    placeholder="Repite la contraseña"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="pl-10 bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3" data-testid="reset-error-message">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
                data-testid="reset-password-button"
              >
                {loading ? 'Actualizando...' : 'Restablecer contraseña'}
              </Button>
            </form>
          )}

          {success && (
            <div className="space-y-4 text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              <h3 className="text-lg font-semibold text-[#0A0A0A]">¡Contraseña actualizada!</h3>
              <p className="text-sm text-[#737373]">
                Tu contraseña fue cambiada correctamente. Te redirigimos al login...
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
